import { createRoute, notFound } from '@tanstack/react-router'
import { lazy } from 'react'
import { listSearch } from '../list-search'
import type { LazyPage } from './lazy-page'
import { guardAt, rosterIsDonors } from './products'
import { rootRoute } from './root'

const MembersPage: LazyPage = lazy(() =>
  import('../../routes/MembersPage').then((m) => ({ default: m.MembersPage })),
)
const AccountPage: LazyPage<{
  id: string
  view: string
  act?: 'give' | 'pay'
}> = lazy(() =>
  import('../../routes/members/AccountPage').then((m) => ({
    default: m.AccountPage,
  })),
)
const DonorPage: LazyPage<{ personId: string }> = lazy(() =>
  import('../../routes/members/DonorPage').then((m) => ({
    default: m.DonorPage,
  })),
)
const StaffPage: LazyPage = lazy(() =>
  import('../../routes/StaffPage').then((m) => ({ default: m.StaffPage })),
)
const MemberModelPage: LazyPage = lazy(() =>
  import('../../routes/members/ModelPage').then((m) => ({
    default: m.ModelPage,
  })),
)
const ChartEditorPage: LazyPage = lazy(() =>
  import('../../routes/seating/ChartEditor').then((m) => ({
    default: m.ChartEditorPage,
  })),
)
const DonorsPage: LazyPage = lazy(() =>
  import('../../routes/DonorsPage').then((m) => ({ default: m.DonorsPage })),
)
const ShulDonorsPage: LazyPage = lazy(() =>
  import('../../routes/members/ShulDonors').then((m) => ({
    default: m.ShulDonorsPage,
  })),
)
const membersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members',
  component: function RosterRoute() {
    return rosterIsDonors() ? <DonorsPage /> : <MembersPage />
  },
  validateSearch: listSearch,
})
const staffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members/staff',
  component: StaffPage,
})
const memberModelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members/model',
  beforeLoad: guardAt('/members/model'),
  component: MemberModelPage,
})
const seatingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members/seating',
  beforeLoad: guardAt('/members/seating'),
  component: ChartEditorPage,
})
const donorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members/donors',
  beforeLoad: guardAt('/members/donors'),
  component: ShulDonorsPage,
  validateSearch: listSearch,
})
/* A donor standing alone has an address of their own: no household to hang it
   under. The static segment outranks the account routes' dynamic one. */
const donorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members/donor/$personId',
  component: function DonorRoute() {
    const { personId } = donorRoute.useParams()
    return <DonorPage personId={personId} />
  },
})
/*
 * One billing account has an address — a household's, or a person's own — and
 * so do its views, held in the route rather than in the list's state, so a
 * reload keeps it and an account can be linked to. `/members/staff` is
 * declared above and still wins: a static segment outranks a dynamic one.
 */
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members/$id',
  component: function AccountRoute() {
    const { id } = accountRoute.useParams()
    return <AccountPage id={id} view="overview" />
  },
})
/* Only a view an account has answers here. Anything else under `/members/…`,
   such as a path beneath `/members/staff`, is not an account and is not found. */
const ACCOUNT_VIEW_KEYS = new Set([
  'people',
  'ledger',
  'pledges',
  'family',
  'giving',
  /* The address the ledger had before it was split from the pledges, kept so
     a saved link still lands on the ledger. */
  'billing',
])
const accountViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members/$id/$view',
  beforeLoad: ({ params }) => {
    if (!ACCOUNT_VIEW_KEYS.has(params.view)) throw notFound()
    /* `/members/staff/…` and `/members/model/…` are not accounts. */
    if (
      params.id === 'staff' ||
      params.id === 'model' ||
      params.id === 'donors'
    )
      throw notFound()
  },
  /* An act asked for from another view — Record giving from the overview —
     travels in the address: moving between views is a new route, and what a
     component held would not survive it. The panel clears it once opened. */
  validateSearch: (raw: Record<string, unknown>): { act?: 'give' | 'pay' } =>
    raw.act === 'give' || raw.act === 'pay' ? { act: raw.act } : {},
  component: function AccountViewRoute() {
    const { id, view } = accountViewRoute.useParams()
    const { act } = accountViewRoute.useSearch()
    return <AccountPage id={id} view={view} act={act} />
  },
})

export const membersRoutes = [
  donorRoute,
  membersRoute,
  staffRoute,
  memberModelRoute,
  seatingRoute,
  donorsRoute,
  accountRoute,
  accountViewRoute,
]
